import { useCallback, useEffect, useState } from "react";
import { FirestoreService } from "../../../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../../shared/api/firebase/references";

export interface EmailConfiguration {
  id: string;
  email: string;
  secret_id?: string;
  password?: string;
}

interface EmailConfigurationsState {
  data: EmailConfiguration[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and manage email configurations from Firestore.
 *
 * Fetches email configurations on mount and provides a refetch function
 * for manual refresh.
 *
 * @returns Object containing:
 *   - `emailConfigurations` - Array of email configurations
 *   - `loading` - Whether data is being fetched
 *   - `error` - Error message if fetch failed, null otherwise
 *   - `refetch` - Function to manually refetch configurations
 *
 * @example
 * ```tsx
 * const { emailConfigurations, loading, error, refetch } = useEmailConfigurations();
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 *
 * return (
 *   <EmailList
 *     emails={emailConfigurations}
 *     onRefresh={refetch}
 *   />
 * );
 * ```
 */
export const useEmailConfigurations = () => {
  const [state, setState] = useState<EmailConfigurationsState>({
    data: [],
    loading: false,
    error: null,
  });

  const fetchEmailConfigurations = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const emailConfigurations =
        await FirestoreService.getAllDocuments<EmailConfiguration>(
          FirestoreCollectionReference.emailOptions(),
        );
      setState((prev) => ({
        ...prev,
        data: emailConfigurations,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch email configurations",
        data: [],
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchEmailConfigurations();
  }, [fetchEmailConfigurations]);

  return {
    emailConfigurations: state.data,
    loading: state.loading,
    error: state.error,
    refetch: fetchEmailConfigurations,
  };
};
